/**
 * Agent builder (Phase 3) wire types + node-type metadata.
 *
 * Mirrors the developer-API shapes published by the backend agents router
 * (`apps/api/src/routers/agents.py`, task-20) and the canonical node-type
 * constants in `apps/api/src/core/constants.py`. The frontend NEVER hardcodes
 * node-type strings inline — palette entries, canvas node components, and DAG
 * (de)serialization all key off {@link NODE_TYPES} / {@link NODE_TYPE_META}
 * defined here so a backend rename surfaces as a single-file change.
 *
 * Endpoints consumed (api client prepends `config.apiBaseUrl` = `/api`):
 *   - GET    /agents              → {@link AgentListResponse}
 *   - GET    /agents/{id}         → {@link AgentDefinition}
 *   - POST   /agents             → {@link AgentDefinition}
 *   - PUT    /agents/{id}         → {@link AgentDefinition}
 *   - DELETE /agents/{id}         → 204
 *   - POST   /agents/{id}/run    → text/event-stream (see {@link RunEvent})
 *   - GET    /agents/runs        → {@link RunHistoryResponse}
 */

// ---------------------------------------------------------------------------
// Node-type constants (mirror backend core.constants)
// ---------------------------------------------------------------------------

/** Canonical agent DAG node types — mirror backend `NODE_*` constants. */
export const NODE_TYPES = {
  TRIGGER_MANUAL: 'trigger.manual',
  RAG_QUERY: 'rag.query',
  LLM_CALL: 'llm.call',
  CONDITION_IF: 'condition.if',
  TRANSFORM_MAP: 'transform.map',
  OUTPUT_RETURN: 'output.return',
} as const;

export type NodeTypeKey = keyof typeof NODE_TYPES;
export type NodeType = (typeof NODE_TYPES)[NodeTypeKey];

/** Every supported node type as a flat list (palette ordering). */
export const NODE_TYPE_LIST: readonly NodeType[] = [
  NODE_TYPES.TRIGGER_MANUAL,
  NODE_TYPES.RAG_QUERY,
  NODE_TYPES.LLM_CALL,
  NODE_TYPES.CONDITION_IF,
  NODE_TYPES.TRANSFORM_MAP,
  NODE_TYPES.OUTPUT_RETURN,
];

/** Presentation metadata for a node type (palette + canvas node chrome). */
export interface NodeTypeMeta {
  type: NodeType;
  /** Short label shown on the node and in the palette. */
  label: string;
  /** One-line description for the palette. */
  description: string;
  /** Tailwind accent classes (border + header) keyed to the node category. */
  accent: string;
  /** Whether this node accepts an incoming edge (false for triggers). */
  hasInput: boolean;
  /** Whether this node emits an outgoing edge (false for outputs). */
  hasOutput: boolean;
}

/**
 * Node-type presentation registry. Each canvas node component and the palette
 * read their chrome from here so colors/labels stay consistent and centralized.
 */
export const NODE_TYPE_META: Record<NodeType, NodeTypeMeta> = {
  [NODE_TYPES.TRIGGER_MANUAL]: {
    type: NODE_TYPES.TRIGGER_MANUAL,
    label: 'Manual Trigger',
    description: 'Entry point. Supplies the initial run inputs (e.g. query).',
    accent: 'border-emerald-300 bg-emerald-50',
    hasInput: false,
    hasOutput: true,
  },
  [NODE_TYPES.RAG_QUERY]: {
    type: NODE_TYPES.RAG_QUERY,
    label: 'Permission-Aware Retrieval',
    description: 'Retrieves corpus chunks filtered to the running persona.',
    accent: 'border-sky-300 bg-sky-50',
    hasInput: true,
    hasOutput: true,
  },
  [NODE_TYPES.LLM_CALL]: {
    type: NODE_TYPES.LLM_CALL,
    label: 'LLM Call',
    description: 'Synthesizes an answer from upstream context.',
    accent: 'border-violet-300 bg-violet-50',
    hasInput: true,
    hasOutput: true,
  },
  [NODE_TYPES.CONDITION_IF]: {
    type: NODE_TYPES.CONDITION_IF,
    label: 'Condition',
    description: 'Branches the DAG on a boolean expression.',
    accent: 'border-amber-300 bg-amber-50',
    hasInput: true,
    hasOutput: true,
  },
  [NODE_TYPES.TRANSFORM_MAP]: {
    type: NODE_TYPES.TRANSFORM_MAP,
    label: 'Transform',
    description: 'Maps / reshapes the payload flowing through the DAG.',
    accent: 'border-teal-300 bg-teal-50',
    hasInput: true,
    hasOutput: true,
  },
  [NODE_TYPES.OUTPUT_RETURN]: {
    type: NODE_TYPES.OUTPUT_RETURN,
    label: 'Return Result',
    description: 'Terminal node. Shapes the final run output.',
    accent: 'border-rose-300 bg-rose-50',
    hasInput: true,
    hasOutput: false,
  },
};

/** Type guard: is `value` a supported node type? */
export function isNodeType(value: string): value is NodeType {
  return (NODE_TYPE_LIST as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Agent definition wire types (mirror agents.py pydantic models)
// ---------------------------------------------------------------------------

/** A single node in an agent DAG (developer-API shape). */
export interface AgentNode {
  id: string;
  name: string;
  type: string;
  params: Record<string, unknown>;
  position?: { x: number; y: number } | null;
}

/** A directed edge connecting two nodes in an agent DAG. */
export interface AgentEdge {
  id: string;
  source_node_id: string;
  target_node_id: string;
  condition?: string | null;
}

/** A full agent definition (the DAG of typed nodes) returned to clients. */
export interface AgentDefinition {
  id: string;
  name: string;
  description?: string | null;
  version: number;
  nodes: AgentNode[];
  edges: AgentEdge[];
  created_at: string;
  updated_at: string;
}

/** A lightweight agent summary for the list endpoint. */
export interface AgentSummary {
  id: string;
  name: string;
  description?: string | null;
  version: number;
  node_count: number;
  /** Distinct node types (tools) the agent is built from. */
  node_types?: string[];
  updated_at: string;
}

/** Short chip label for a node type (the tool an agent uses). */
const NODE_SHORT_LABEL: Record<string, string> = {
  'trigger.manual': 'Trigger',
  'rag.query': 'Retrieval',
  'llm.call': 'LLM',
  'condition.if': 'Condition',
  'transform.map': 'Transform',
  'output.return': 'Output',
};

export function toolShortLabel(nodeType: string): string {
  return NODE_SHORT_LABEL[nodeType] ?? nodeType;
}

/** Response for `GET /agents`. */
export interface AgentListResponse {
  agents: AgentSummary[];
  total: number;
}

/** Request body for `POST /agents` and `PUT /agents/{id}`. */
export interface SaveAgentRequest {
  name: string;
  description?: string | null;
  nodes: AgentNode[];
  edges: AgentEdge[];
}

/** Request body for `POST /agents/{id}/run`. */
export interface RunAgentRequest {
  input_payload: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Run history wire types
// ---------------------------------------------------------------------------

/** A single entry in the run history (`GET /agents/runs`). */
export interface RunRecord {
  run_id: string;
  agent_id: string;
  agent_name: string;
  actor: string;
  actor_groups: string[];
  status: string;
  steps_completed: number;
  steps_failed: number;
  output_payload?: Record<string, unknown> | null;
  started_at: string;
  finished_at?: string | null;
}

/** Response for `GET /agents/runs`. */
export interface RunHistoryResponse {
  runs: RunRecord[];
  total: number;
}

// ---------------------------------------------------------------------------
// SSE run events (mirror the orchestrator event dicts streamed by /run)
// ---------------------------------------------------------------------------

/** Orchestrator event types streamed over SSE from `POST /agents/{id}/run`. */
export const RUN_EVENT_TYPES = {
  RUN_STARTED: 'run.started',
  STEP_STARTED: 'step.started',
  STEP_COMPLETED: 'step.completed',
  STEP_FAILED: 'step.failed',
  RUN_COMPLETED: 'run.completed',
  RUN_FAILED: 'run.failed',
  RUN_CANCELLED: 'run.cancelled',
} as const;

export type RunEventType = (typeof RUN_EVENT_TYPES)[keyof typeof RUN_EVENT_TYPES];

/**
 * A decoded SSE run event. The orchestrator emits one JSON dict per event with
 * a discriminating `event_type`, a hash-chain (`hash`/`prev_hash`), and
 * per-event fields. Optional fields are present per the event_type:
 *   - step.*: `node_id`, `node_name`, `status`, `batch_index`, plus
 *     `duration_ms`/`output` (completed) or `error`/`error_type` (failed).
 *   - run.*: `status`, `total_steps`/`total_batches` (started) or
 *     `steps_completed`/`steps_failed`/`output_payload`/`duration_ms` (terminal).
 */
export interface RunEvent {
  event_type: string;
  run_id: string;
  event_id?: string;
  timestamp?: string;
  hash?: string;
  prev_hash?: string | null;
  // step.* fields
  step_id?: string;
  node_id?: string;
  node_name?: string;
  status?: string;
  batch_index?: number;
  duration_ms?: number;
  output?: Record<string, unknown> | null;
  // run.* fields
  agent_definition_id?: string;
  total_steps?: number;
  total_batches?: number;
  steps_completed?: number;
  steps_failed?: number;
  output_payload?: Record<string, unknown> | null;
  // error fields (step.failed / run.failed)
  error?: string;
  error_type?: string;
}

/** True for the terminal run events (completed / failed / cancelled). */
export function isTerminalRunEvent(eventType: string): boolean {
  return (
    eventType === RUN_EVENT_TYPES.RUN_COMPLETED ||
    eventType === RUN_EVENT_TYPES.RUN_FAILED ||
    eventType === RUN_EVENT_TYPES.RUN_CANCELLED
  );
}
